import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, LogOut, CheckCircle, Clock, Calendar, Edit, Bell, Send, Mail, ExternalLink } from 'lucide-react';
import FamilyScheduleDelivery from '@/components/FamilyScheduleDelivery';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function FamilyPortal() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingReady, setSubmittingReady] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchFamilies();
  }, []);

  const fetchFamilies = async () => {
    try {
      const response = await api.get('/families');
      setFamilies(response.data);
      
      // If family user has no profile, redirect to complete profile
      if (response.data.length === 0) {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user.role === 'family') {
            navigate('/complete-profile');
            return;
          }
        }
      }
    } catch (error) {
      toast.error('Failed to fetch family data');
    } finally {
      setLoading(false);
    }
  };

  const handleReadyToSchedule = async (familyId) => {
    setSubmittingReady(true);
    try {
      const response = await api.post(`/families/${familyId}/ready-to-schedule`);
      toast.success(response.data.message);
      fetchFamilies(); // Refresh to update the UI
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingReady(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="family-portal">
      <div className="bg-primary text-white py-6 px-8">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8" />
            <h1 className="text-2xl font-bold">The Bookshelf Project</h1>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://www.thebookshelfproject.org"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-white/90 hover:text-white hover:bg-white/10 px-3 py-2 rounded-md transition-colors"
              data-testid="back-to-main-site-link"
            >
              <ExternalLink className="w-4 h-4" />
              Back to main site
            </a>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-2">Welcome{user ? `, ${user.username}` : ''}!</h2>
          <p className="text-stone-600">Track your family's bookshelf delivery status</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : families.length === 0 ? (
          <Card className="border-stone-200 shadow-card">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-600 mb-2">No family profiles found</p>
              <p className="text-sm text-stone-500">Please contact the administrator to register your family</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="family-profiles-list">
            {families.map((family) => (
              <div key={family.id}>
                <Card className="border-stone-200 shadow-card" data-testid={`family-profile-${family.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-2xl mb-2">
                          {family.parent_first_name} {family.parent_last_name}
                        </CardTitle>
                        <div className="flex items-center gap-2 flex-wrap">
                          {family.delivery_status === 'delivered' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-yellow-600" />
                          )}
                          <Badge
                            data-testid={`status-badge-${family.id}`}
                            className={family.delivery_status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
                          >
                            {family.delivery_status === 'delivered' ? 'Delivered' : 'Pending Delivery'}
                          </Badge>
                          {family.ready_to_schedule && !family.scheduling_invited && (
                            <Badge className="bg-purple-100 text-purple-800">
                              <Bell className="w-3 h-3 mr-1" />
                              Ready to Schedule
                            </Badge>
                          )}
                          {family.scheduling_invited && (
                            <Badge className="bg-blue-100 text-blue-800">
                              <Calendar className="w-3 h-3 mr-1" />
                              Invited to Schedule
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Link to="/edit-family-profile">
                        <Button variant="outline" className="rounded-full" data-testid="edit-profile-button">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Profile
                        </Button>
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-stone-600 mb-1">Email</p>
                          <p className="font-medium" data-testid="family-email">{family.email}</p>
                        </div>
                        <div>
                          <p className="text-sm text-stone-600 mb-1">Phone</p>
                          <p className="font-medium" data-testid="family-phone">{family.phone}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {family.parent_role && (
                          <div>
                            <p className="text-sm text-stone-600 mb-1">Role</p>
                            <p className="font-medium" data-testid="family-role">{family.parent_role}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-stone-600 mb-1">Children</p>
                          <p className="font-medium">{family.children.length} child{family.children.length !== 1 ? 'ren' : ''}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-stone-600 mb-1">Location</p>
                          <p className="font-medium">{family.location_type}: {family.location_detail}</p>
                          {family.ward_number && <p className="text-sm text-stone-600">Ward {family.ward_number}</p>}
                          {family.city_county && <p className="text-sm text-stone-600">{family.city_county}</p>}
                        </div>
                        {family.created_at && (
                          <div>
                            <p className="text-sm text-stone-600 mb-1">Member Since</p>
                            <p className="font-medium" data-testid="member-since">
                              {new Date(family.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        )}
                      </div>

                      {family.delivery_status === 'delivered' && family.delivery_confirmed_at && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm font-medium text-green-900 mb-1">Delivery Confirmed</p>
                          <p className="text-sm text-green-700">
                            Delivered on {new Date(family.delivery_confirmed_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      )}

                      {/* Profile Submitted - Awaiting scheduling invitation */}
                      {family.delivery_status === 'pending' && !family.scheduling_invited && (
                        <div className="p-6 bg-gradient-to-r from-blue-50 to-sky-50 border-2 border-blue-200 rounded-xl">
                          <div className="text-center">
                            <Mail className="w-10 h-10 text-primary mx-auto mb-3" />
                            <h3 className="text-lg font-bold text-primary mb-2">Profile Complete!</h3>
                            <p className="text-stone-600 mb-4 max-w-md mx-auto">
                              Thank you for completing your family profile. You will receive a <strong>scheduling invitation</strong> within 24-48 hours to pick your bookshelf delivery date and time.
                            </p>
                            <div className="bg-white/60 rounded-lg p-4 max-w-sm mx-auto">
                              <p className="text-sm text-blue-700">
                                <Clock className="w-4 h-4 inline mr-1" />
                                Keep an eye on your email for your scheduling invitation!
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div>
                        <p className="text-sm text-stone-600 mb-2">Children Details</p>
                        <div className="space-y-2">
                          {family.children.map((child, index) => (
                            <div key={index} className="p-3 bg-background rounded-lg" data-testid={`child-${index}`}>
                              <p className="font-medium">{child.name}, Age {child.age}</p>
                              <p className="text-sm text-stone-600">
                                {child.learning_stage} • Reading Level: {child.reading_level}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Profile Questions Section */}
                      <div className="border-t pt-4 mt-4">
                        <p className="text-sm text-stone-600 mb-3 font-medium">Profile Preferences</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div className="p-2 bg-stone-50 rounded-lg">
                            <p className="text-xs text-stone-500">Library Card</p>
                            <p className="font-medium text-sm" data-testid="has-library-card">{family.has_library_card ? 'Yes' : 'No'}</p>
                          </div>
                          <div className="p-2 bg-stone-50 rounded-lg">
                            <p className="text-xs text-stone-500">Has Bookshelf</p>
                            <p className="font-medium text-sm" data-testid="has-bookshelf">{family.already_has_bookshelf ? 'Yes' : 'No'}</p>
                          </div>
                          <div className="p-2 bg-stone-50 rounded-lg">
                            <p className="text-xs text-stone-500">Books from Birth</p>
                            <p className="font-medium text-sm" data-testid="books-from-birth">{family.books_from_birth_signup ? 'Yes' : 'No'}</p>
                          </div>
                          <div className="p-2 bg-stone-50 rounded-lg">
                            <p className="text-xs text-stone-500">SMS/Text Consent</p>
                            <p className="font-medium text-sm" data-testid="sms-consent">{family.sms_consent ? 'Yes' : 'No'}</p>
                          </div>
                          <div className="p-2 bg-stone-50 rounded-lg">
                            <p className="text-xs text-stone-500">Photo/Video Consent</p>
                            <p className="font-medium text-sm" data-testid="photo-video-consent">{family.photo_video_consent ? 'Yes' : 'No'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {family.scheduling_invited && family.delivery_status === 'pending' && (
                  <div className="mt-6">
                    <FamilyScheduleDelivery family={family} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}